import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Flame } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SELECTORS, ethCall, toWholeUnits } from "@/lib/bsc";

const STR = {
  en: {
    kicker: "Live Burn Counter",
    title: "Zeros Already Killed",
    label: "SLN sent to the dead wallet, verifiable on-chain",
    live: "Live on-chain data",
    unavailable: "Data unavailable",
  },
  zh: {
    kicker: "实时销毁计数",
    title: "已被消灭的零",
    label: "已发送至销毁地址的 SLN,可在链上验证",
    live: "链上实时数据",
    unavailable: "暂无数据",
  },
  fr: {
    kicker: "Compteur de burn en direct",
    title: "Zéros déjà éliminés",
    label: "SLN envoyés à l'adresse dead, vérifiables on-chain",
    live: "Données on-chain en direct",
    unavailable: "Données indisponibles",
  },
};

export default function BurnCounter() {
  const { lang } = useLanguage();
  const s = STR[lang] || STR.en;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [burned, setBurned] = useState(null);
  const [failed, setFailed] = useState(false);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      const [decHex, deadHex] = await Promise.all([
        ethCall(SELECTORS.decimals, ctrl.signal),
        ethCall(SELECTORS.deadBalance, ctrl.signal),
      ]);
      if (ctrl.signal.aborted) return;
      const decimals = decHex ? parseInt(decHex, 16) : null;
      const whole = deadHex && decimals != null ? toWholeUnits(deadHex, decimals) : null;
      if (whole == null) setFailed(true);
      else setBurned(Number(whole));
    })();
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    if (!inView || burned == null) return undefined;
    const controls = animate(0, burned, {
      duration: 2.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.floor(v).toLocaleString("en-US")),
    });
    return () => controls.stop();
  }, [inView, burned]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden border-b border-red-900/25 bg-[#0D0608] px-4 py-16 sm:px-6 lg:px-8"
      data-testid="burn-counter-section"
      aria-label={s.kicker}
    >
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[40vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(220,38,38,0.14),transparent_65%)]"
      />
      <div className="relative mx-auto flex max-w-5xl flex-col items-center gap-4 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-red-800/60 bg-red-950/50 text-red-500 shadow-[0_0_30px_rgba(255,30,39,0.35)]">
          <Flame size={26} aria-hidden="true" />
        </span>
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-red-400" data-testid="burn-counter-kicker">
          {s.kicker}
        </p>
        <p
          className="bg-gradient-to-r from-yellow-300 via-yellow-500 to-red-500 bg-clip-text font-mono text-5xl font-bold text-transparent sm:text-7xl"
          data-testid="burn-counter-value"
          aria-live="polite"
        >
          {failed ? s.unavailable : burned == null ? "…" : display}
        </p>
        <h2 className="font-display text-lg font-bold uppercase tracking-wide text-zinc-100 sm:text-xl" data-testid="burn-counter-title">
          {s.title}
        </h2>
        <p className="max-w-md text-sm text-zinc-500">{s.label}</p>
        <p className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          {s.live}
        </p>
      </div>
    </section>
  );
}
